import { env } from 'next-runtime-env';

import {
	LevelMap,
	type LoggerServices,
	LogLevel,
	type TLoggerMessages,
} from '../shared';

interface Logger {
	log: (
		logLevel: LogLevel,
		service: LoggerServices,
		message?: TLoggerMessages,
		payload?: Record<string, unknown>
	) => void;
}

let clientLogger: Logger;
let serverLogger: Logger;

const getClientLogger = async () => {
	if (!clientLogger) {
		clientLogger = (await import('../ClientLogger')).default as Logger;
	}
	return clientLogger;
};

const getServerLogger = async () => {
	if (!serverLogger) {
		serverLogger = (await import('../ServerLogger')).default as Logger;
	}
	return serverLogger;
};

class IsomorphicLogger {
	static log = async (
		level: LogLevel,
		service: LoggerServices,
		message?: TLoggerMessages,
		payload?: Record<string, unknown>
	) => {
		const numericLevel = LevelMap[level];
		const allowedLevel = (env('NEXT_PUBLIC_LOG_LEVEL') as LogLevel) ?? 'warn';
		const numericAllowedLevel = LevelMap[allowedLevel];
		if (numericLevel < numericAllowedLevel) {
			return;
		}
		try {
			if (typeof window === 'undefined') {
				const logger = await getServerLogger();
				logger.log(level, service, message, {
					service,
					...payload,
				});
			} else {
				const logger = await getClientLogger();
				logger.log(level, service, message, payload);
			}
		} catch {}
	};

	static error = (
		service: LoggerServices,
		message?: TLoggerMessages,
		payload?: Record<string, unknown>
	) => void IsomorphicLogger.log(LogLevel.error, service, message, payload);
	static warn = (
		service: LoggerServices,
		message?: TLoggerMessages,
		payload?: Record<string, unknown>
	) => void IsomorphicLogger.log(LogLevel.warn, service, message, payload);
	static info = (
		service: LoggerServices,
		message?: TLoggerMessages,
		payload?: Record<string, unknown>
	) => void IsomorphicLogger.log(LogLevel.info, service, message, payload);
	static debug = (
		service: LoggerServices,
		message?: TLoggerMessages,
		payload?: Record<string, unknown>
	) => void IsomorphicLogger.log(LogLevel.debug, service, message, payload);
}

export { IsomorphicLogger as DatadogLogger };
